import React, { useEffect, useMemo, useState } from "react";
import { Search, SearchX } from "lucide-react";
import servicesApi from "api/service/service.api";
import operatorApi from "api/operator/operator.api";
import { addToCart } from "store/features/cartSlice";
import { useDispatch } from "react-redux";
import CartModal from "components/Cart/CartModal";
import CartBottomBar from "components/Menu/CartBottomBar";
import MenuItemFresha from "components/Menu/MenuItemFresha";
import Loading from "Util/Loading";
import ProcessingBar from "Util/Processingbar";

const SearchPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [operator, setOperator] = useState(null);
  const [modalItem, setModalItem] = useState(null); // 🔥 highlight

  const highlightMatch = (text, keyword) => {
    if (!keyword) return text;

    const regex = new RegExp(`(${keyword})`, "gi");

    return text.split(regex).map((part, i) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <mark key={i} className="bg-neonYellow text-navy">
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      ),
    );
  };
  const highlightText = (text, keyword) => {
    if (!keyword || !text) return text;

    const regex = new RegExp(`(${keyword})`, "gi");

    return text.split(regex).map((part, i) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <span
          key={i}
          className="bg-neonYellow text-navy px-0 rounded font-semibold"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // 🔥 LOAD REAL DATA
  const getOperatorData = async () => {
    try {
      const slug = "saigon-star";

      const { response, err } = await operatorApi.getPublicOperator(slug);

      if (response) {
        const operatorData = response.data;

        setOperator(operatorData);

        return operatorData; // ⭐ IMPORTANT
      } else {
        console.error("No operator data found");
      }
    } catch (error) {
      console.error("Error fetching operator:", error);
    }
  };

  const getMenuCategoryData = async () => {
    try {
      const { response, err } = await servicesApi.getMenuCategoryPublic();

      if (response && response.data) {
        const categories = response.data.categories;

        setMenuCategories(categories);
      } else {
        console.error("No categories found");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getMenuItemsData = async (operatorId) => {
    try {
      if (!operatorId) {
        console.warn("No operatorId provided");
        return;
      }

      const { response } = await servicesApi.getFoodItemsPublic({
        operatorId,
      });

      if (response?.data?.success) {
        const original_items = response.data.foodItems;

        setMenuItems(original_items);
      }
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  // LOAD ALL DATA
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // 1️⃣ Load operator first
        const operatorData = await getOperatorData();

        // 2️⃣ Then load categories + items in parallel
        await Promise.all([
          getMenuCategoryData(),
          getMenuItemsData(operatorData?._id),
        ]);
      } catch (error) {
        console.error("Error loading menu data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  // 🔥 FILTER

  const filtered = useMemo(() => {
    if (!keyword.trim()) return [];

    return menuItems.filter((item) =>
      (item.title || "").toLowerCase().includes(keyword.toLowerCase()),
    );
  }, [menuItems, keyword]);

  const groupedItems = useMemo(() => {
    const grouped = {};

    menuCategories.forEach((cat) => {
      grouped[cat._id] = filtered.filter(
        (item) => item.category?._id === cat._id,
      );
    });

    return grouped;
  }, [filtered, menuCategories]);

  const dispatch = useDispatch();

  return (
    <div className="min-h-screen bg-darkbg text-text pt-[120px] px-4">
      {/* 🔥 PROCESSING BAR */}
      {isLoading && (
        <div className="fixed top-0 left-0 w-full h-[2px] bg-gray-800 z-[9999]">
          <div className="h-full bg-neonYellow animate-[loading_1.2s_linear_infinite]" />
        </div>
      )}

      {/* 🔥 HERO SEARCH */}
      <div className="max-w-6xl mx-auto text-center mb-10">
        <div className="flex items-center gap-3 border border-gray-600 rounded-xl px-4 py-3 ">
          <Search className="text-neonYellow" />
          <input
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search Pho, Rice, Spring roll..."
            className="flex-1 bg-transparent outline-none  placeholder-gray-400"
          />
        </div>

        {/* result count */}
        {!isLoading && (
          <p className="text-sm text-gray-400 mt-3">
            {filtered.length} results
          </p>
        )}
      </div>

      {/* 🔥 RESULTS */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-1 gap-4">
        {/* 🔥 LOADING STATE */}
        {isLoading && <ProcessingBar />}

        {!isLoading && keyword.trim() && filtered.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center mt-20 text-center">
            {/* ICON */}
            <SearchX className="text-yellowstar m-4" size={30} />

            {/* TITLE */}
            <div className="text-navy text-lg font-semibold mb-1 ">
              No matching dishes
            </div>

            {/* DESCRIPTION */}
            <div className="text-gray-500 text-sm max-w-sm">
              We couldn’t find anything for{" "}
              <span className="text-neonYellow font-medium">"{keyword}"</span>.
              Try a different search term.
            </div>
          </div>
        )}

        {menuCategories.map((cat) => {
          const items = groupedItems[cat._id] || [];

          if (!keyword.trim() || items.length === 0) return null;

          return (
            <div key={cat._id} className="mb-8">
              {/* 🔥 CATEGORY HEADER */}
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold uppercase text-text">
                  {cat.name}
                </h3>

                {cat.image && (
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-24 h-16 object-cover rounded-md"
                  />
                )}
              </div>

              {/* 🔥 ITEMS */}
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item._id} className="mb-2">
                    <MenuItemFresha
                      item={item}
                      highlightMatch={highlightMatch}
                      searchTerm={keyword} // 🔥 IMPORTANT
                      isSelected={false}
                      onClick={() => setModalItem(item)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {modalItem && (
          <CartModal item={modalItem} onClose={() => setModalItem(null)} />
        )}

        <CartBottomBar />
      </div>
    </div>
  );
};

export default SearchPage;
